import { useFlag } from '@sketch/modules-common'
import { useApolloClient } from '@apollo/client'
import {
  useCollectionSearchArgument,
  useHideCollectionsSetting,
} from '../../collections/hooks'
import { DragAndDroppedShareIds } from './useShareDragAndDrop'
import {
  addCollectionToProject,
  createOptimisticCollection,
  removeSharesFromProject,
} from '../../collections/operations'

interface DropShareToCollectionProps {
  projectIdentifier?: string
}

export const useDropShareToCollection = ({
  projectIdentifier,
}: DropShareToCollectionProps) => {
  const isNestedProjectsOn = useFlag('nested-projects')

  const { setHideCollections } = useHideCollectionsSetting()
  const collectionSearch = useCollectionSearchArgument()

  const cache = useApolloClient()

  const droppedShareToCollection = ({
    draggedShareId,
    droppedShareId,
  }: DragAndDroppedShareIds) => {
    // We don't want to create collections when nested projects FF is enabled,
    // in this case we'll need to create a nested project with the 2 shares inside
    if (isNestedProjectsOn) {
      return
    }

    // we need a valid project id to create a collection by drag and drop
    if (!projectIdentifier) {
      return
    }

    if (draggedShareId === droppedShareId) {
      // If share was dropped on itself, ignore
      return
    }

    // We need to show collections in case they are hidden
    // As otherwise, we will not be able to display a collection placeholder
    // which is requesting to enter a name for the new collection
    // see https://linear.app/sketch/issue/SWEB-908/hidden-collections-conflict-with-the-draganddrop-functionality
    setHideCollections(false)

    const optimisticCollection = createOptimisticCollection({
      cache,
      shareIds: [draggedShareId, droppedShareId],
    })

    addCollectionToProject({
      cache,
      projectIdentifier,
      search: collectionSearch,
      collection: {
        __typename: 'Collection',
        ...optimisticCollection,
      },
    })

    removeSharesFromProject(cache, projectIdentifier, [
      { identifier: draggedShareId },
      { identifier: droppedShareId },
    ])
  }

  return droppedShareToCollection
}
